import React, { useState, useRef, useEffect } from 'react';
import '../../styles/AddSellerModal.css';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../config/apiConfig.js';
import fetchApi from '../../utils/api.js';
import Swal from 'sweetalert2';


const CreatableSelect = React.forwardRef(({ value, onChange, options, onAddOption, placeholder, onSelect, onPrevField, onNextField, creatable = true }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isOpen && activeIndex >= 0 && dropdownRef.current) {
            const activeEl = dropdownRef.current.children[activeIndex];
            if (activeEl) {
                activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [activeIndex, isOpen]);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        onChange(newVal);
        setIsOpen(true);
        setActiveIndex(0);
    };

    const handleOptionClick = (opt) => {
        onChange(opt);
        setSearchTerm(opt);
        setIsOpen(false);
        setActiveIndex(-1);
        if (onSelect) onSelect();
    };

    const handleBlur = (e) => {
        // Use a small delay to allow clicking on options in the dropdown
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setIsOpen(false);
            }
        }, 150);
    };

    const handleAddClick = () => {
        onAddOption(searchTerm);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const filteredOptions = (options || []).filter(opt =>
        opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const showAddOption = creatable && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            } else if (e.key === 'ArrowLeft' && onPrevField) {
                if (!searchTerm || e.target.selectionStart === 0) {
                    e.preventDefault();
                    onPrevField();
                }
            } else if (e.key === 'ArrowRight' && onNextField) {
                if (!searchTerm || e.target.selectionStart === searchTerm.length) {
                    e.preventDefault();
                    onNextField();
                }
            }
            return;
        }

        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % totalOptions);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + totalOptions) % totalOptions);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                const opt = filteredOptions[activeIndex];
                handleOptionClick(opt);
            } else if (showAddOption && activeIndex === filteredOptions.length) {
                handleAddClick();
            } else {
                setIsOpen(false);
                if (onSelect) onSelect();
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        } else if (e.key === 'ArrowLeft' && onPrevField) {
            if (!searchTerm || e.target.selectionStart === 0) {
                e.preventDefault();
                setIsOpen(false);
                onPrevField();
            }
        } else if (e.key === 'ArrowRight' && onNextField) {
            if (!searchTerm || e.target.selectionStart === searchTerm.length) {
                e.preventDefault();
                setIsOpen(false);
                onNextField();
            }
        }
    };

    return (
        <div className="custom-select-container" ref={containerRef} style={{ width: '100%' }}>
            <input
                ref={ref}
                className="add-seller-input"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsOpen(true)}
                onBlur={handleBlur}
                placeholder={placeholder || "Select or Type..."}
                style={{ width: '100%' }}
            />
            {isOpen && (filteredOptions.length > 0 || showAddOption) && (
                <div className="custom-select-dropdown" ref={dropdownRef}>
                    {filteredOptions.map((opt, index) => (
                        <div
                            key={opt}
                            className="custom-select-option"
                            style={{ backgroundColor: index === activeIndex ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault(); // maintain focus on input
                                handleOptionClick(opt);
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                    {showAddOption && (
                        <div
                            className="custom-select-add-option"
                            style={{ backgroundColor: activeIndex === filteredOptions.length ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddClick();
                            }}
                        >
                            Add "{searchTerm}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

function AddSellerModal({ isOpen, onClose, isPage, onSuccess }) {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';

    const [formData, setFormData] = useState({
        sellerGstNumber: '',
        sellerTradeName: '',
        sellerName: '',
        sellerPrefix: '',
        sellerEmail: '',
        sellerMobileNumber: '',
        sellerCountry: '',
        sellerState: '',
        sellerCity: '',
        sellerPinCode: '',
        sellerStateCode: '',
        sellerPanCardNumber: '',
        sellerCinNumber: '',
        sellerBankAccountName: '',
        sellerBankName: '',
        sellerAccountNo: '',
        sellerIfscCode: '',
        sellerTypeId: '',
        saleTypeId: '',
        sellerBankAddress: '',
        sellerAddress: '',
        company_id: isSuperAdmin ? '' : (user?.company_id || sessionStorage.getItem('company_id') || '')
    });

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [sellerTypes, setSellerTypes] = useState([]);
    const [saleTypes, setSaleTypes] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [loading, setLoading] = useState(false);

    // Refs for keyboard navigation
    const gstRef = useRef(null);
    const tradeNameRef = useRef(null);
    const sellerNameRef = useRef(null);
    const prefixRef = useRef(null);
    const emailRef = useRef(null);
    const companyRef = useRef(null);
    const mobileRef = useRef(null);
    const countryRef = useRef(null);
    const stateRef = useRef(null);
    const cityRef = useRef(null);
    const pinRef = useRef(null);
    const stateCodeRef = useRef(null);
    const panRef = useRef(null);
    const cinRef = useRef(null);
    const bankAccountNameRef = useRef(null);
    const bankNameRef = useRef(null);
    const accountNoRef = useRef(null);
    const ifscRef = useRef(null);
    const sellerTypeRef = useRef(null);
    const bankAddrRef = useRef(null);
    const addrRef = useRef(null);
    const submitRef = useRef(null);


    // Auto-focus GST on open
    useEffect(() => {
        if (isOpen || isPage) {
            setTimeout(() => {
                gstRef.current?.focus();
            }, 100);
        }
    }, [isOpen, isPage]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        const token = sessionStorage.getItem('token');
        try {
            // Fetch Seller Types (using CustomerType model for consistency or if separate model exists)
            const stRes = await fetch(`${API_BASE_URL}/customer-type?type=seller`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (stRes.ok) {
                const data = await stRes.json();
                setSellerTypes(data.customerTypes || (Array.isArray(data) ? data : []));
            }

            // Fetch Sale Types
            const saleRes = await fetch(`${API_BASE_URL}/sale-type`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (saleRes.ok) {
                const data = await saleRes.json();
                setSaleTypes(data.saleTypes || (Array.isArray(data) ? data : []));
            }

            // Fetch Companies if Super Admin
            if (isSuperAdmin) {
                const cRes = await fetch(`${API_BASE_URL}/company`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (cRes.ok) {
                    const data = await cRes.json();
                    setCompanies(data.companies || (Array.isArray(data) ? data : []));
                }
            }

            // Fetch Countries
            const countryRes = await fetch(`${API_BASE_URL}/country`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (countryRes.ok) {
                const data = await countryRes.json();
                const countries = Array.isArray(data) ? data : (data.countries || []);
                setAllCountries(countries);
                setCountryList(countries.map(c => c.country_name || c.countryName));
            }
        } catch (error) {
            console.error("Error fetching initial data:", error);
        }
    };

    useEffect(() => {
        const fetchStates = async () => {
            const country = allCountries.find(c => (c.country_name || c.countryName) === formData.sellerCountry);
            if (country) {
                const token = sessionStorage.getItem('token');
                try {
                    const res = await fetch(`${API_BASE_URL}/state?country_id=${country.id || country.countryId || country._id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const list = Array.isArray(data) ? data : (data.states || []);
                        setAllStates(list);
                        setStateList(list.map(s => s.state_name || s.stateName));
                    }
                } catch (error) {
                    console.error("Error fetching states:", error);
                }
            } else {
                setStateList([]);
            }
        };
        fetchStates();
    }, [formData.sellerCountry, allCountries]);

    useEffect(() => {
        const fetchCities = async () => {
            const state = allStates.find(s => (s.state_name || s.stateName) === formData.sellerState);
            if (state) {
                const token = sessionStorage.getItem('token');
                try {
                    const res = await fetch(`${API_BASE_URL}/city?state_id=${state.id || state.stateId || state._id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const list = Array.isArray(data) ? data : (data.cities || []);
                        setCityList(list.map(c => c.city_name || c.cityName));
                    }
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCityList([]);
            }
        };
        fetchCities();
    }, [formData.sellerState, allStates]);

    const handleAddCountry = async (newCountryName) => {
        if (!newCountryName) return;

        // Optimistic update or wait? Let's wait to ensure it saved.
        try {
            const token = sessionStorage.getItem('token');
            const code = newCountryName.substring(0, 3).toUpperCase();

            const response = await fetch(`${API_BASE_URL}/country`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ countryName: newCountryName, countryCode: code })
            });

            if (response.ok) {
                const data = await response.json();
                setAllCountries(prev => [...prev, data.country]);
                setCountryList(prev => [...prev, data.country.countryName]);
                toast.success(`Country "${newCountryName}" added successfully`);
            } else {
                // checking if error is "exists" might be good, but toast error works
                const err = await response.json();
                toast.error(err.message || "Failed to add country");
            }
        } catch (error) {
            console.error("Add Country Error:", error);
            toast.error("Server error while adding country");
        }
    };

    const handleAddState = async (newStateName) => {
        if (!newStateName) return;
        const country = allCountries.find(c => (c.country_name || c.countryName) === formData.sellerCountry);
        if (!country) {
            toast.warning("please select country then add new state");
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/state`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ state_name: newStateName, country_id: country.id || country.countryId })
            });
            if (res.ok) {
                const data = await res.json();
                const s = data.state || data;
                setAllStates(prev => [...prev, s]);
                setStateList(prev => [...prev, s.state_name || s.stateName]);
                toast.success(`State "${newStateName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add state");
            }
        } catch (error) {
            console.error("Add State Error:", error);
            toast.error("Server error while adding state");
        }
    };

    const handleAddCity = async (newCityName) => {
        if (!newCityName) return;
        const state = allStates.find(s => (s.state_name || s.stateName) === formData.sellerState);
        if (!state) {
            toast.warning("please select state or country then add new city", {
                style: { backgroundColor: '#ffcc00', color: 'black' }
            });
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/city`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ city_name: newCityName, state_id: state.id || state.stateId })
            });

            if (res.ok) {
                const data = await res.json();
                const c = data.city || data;
                setCityList(prev => [...prev, c.city_name || c.cityName]);
                toast.success(`City "${newCityName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add city");
            }
        } catch (error) {
            console.error("Add City Error:", error);
            toast.error("Server error while adding city");
        }
    };

    const handleAddSellerType = async (newTypeName) => {
        if (!newTypeName) return;
        try {
            const token = sessionStorage.getItem('token');
            const compId = formData.company_id || sessionStorage.getItem('company_id') || user?.company_id;
            const res = await fetch(`${API_BASE_URL}/customer-type`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ customerTypeName: newTypeName, company_id: compId, type: 'seller' })
            });
            if (res.ok) {
                const data = await res.json();
                const ct = data.customerType || data;
                setSellerTypes(prev => [...prev, ct]);
                setFormData(prev => ({ ...prev, sellerTypeId: ct.id || ct._id || ct.customerTypeId }));
                toast.success(`Seller Type "${newTypeName}" added successfully`);
            } else {
                toast.error("Failed to add seller type");
            }
        } catch (error) {
            console.error("Add Seller Type Error:", error);
            toast.error("Server error while adding seller type");
        }
    };

    const [isVerifyingGst, setIsVerifyingGst] = useState(false);

    const handleGSTVerify = async (gstToVerify) => {
        const gst = (gstToVerify || '').trim().toUpperCase();
        if (!gst) {
            toast.warn('Please enter a GST number');
            return;
        }
        if (gst.length !== 15) {
            toast.warn('GST number must be exactly 15 characters');
            return;
        }

        setIsVerifyingGst(true);
        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gst/verify/${gst}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (res.ok && data.success && data.data) {
                const info = data.data;
                toast.success(`GST verified: ${info.legalName || info.tradeName}`);

                // Extract State Code from GSTIN (first 2 digits)
                const stateCode = gst.substring(0, 2);

                // Auto-determine Seller Type based on Constitution of Business
                let matchedSellerTypeId = '';
                const companyType = info.businessType || info.taxpayerType || '';
                if (companyType) {
                    const matchedType = sellerTypes.find(t => {
                        const name = (t.customer_type_name || t.customerTypeName || t.name || '').toLowerCase();
                        return name === companyType.toLowerCase();
                    });
                    if (matchedType) {
                        matchedSellerTypeId = matchedType.id || matchedType._id || matchedType.customerTypeId;
                    } else {
                        try {
                            const compId = formData.company_id || sessionStorage.getItem('company_id') || user?.company_id;
                            const addRes = await fetchApi('/customer-type', {
                                method: 'POST',
                                body: JSON.stringify({ customerTypeName: companyType, company_id: compId, type: 'seller' })
                            });
                            if (addRes && addRes.customerType) {
                                setSellerTypes(prev => [...prev, addRes.customerType]);
                                matchedSellerTypeId = addRes.customerType.id || addRes.customerType._id || addRes.customerType.customerTypeId;
                            }
                        } catch (e) { console.error("Auto add seller type error", e); }
                    }
                }

                // Auto-determine Sale Type based on Company State vs Seller State
                let matchedSaleTypeId = '';
                if (saleTypes && saleTypes.length > 0) {
                    const savedCompanyStr = sessionStorage.getItem('company');
                    const companyObj = savedCompanyStr ? JSON.parse(savedCompanyStr) : null;
                    const companyGst = companyObj?.companyGstNumber || '';
                    const companyStateCode = companyGst ? companyGst.substring(0, 2) : '';

                    const isLocal = !companyStateCode || stateCode === companyStateCode;
                    const targetKeyword = isLocal ? '9%' : '18%';

                    const matchedSaleType = saleTypes.find(s => {
                        const name = (s.saleTypeName || s.name || '').toLowerCase();
                        return name.includes(targetKeyword);
                    });

                    if (matchedSaleType) {
                        matchedSaleTypeId = matchedSaleType._id || matchedSaleType.id;
                    } else {
                        matchedSaleTypeId = saleTypes[0]._id || saleTypes[0].id;
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    sellerGstNumber: info.gstNumber || prev.sellerGstNumber,
                    sellerTradeName: info.tradeName || info.legalName || prev.sellerTradeName,
                    sellerName: info.tradeName || info.legalName || prev.sellerName,
                    sellerAddress: info.address || prev.sellerAddress,
                    sellerState: info.state || prev.sellerState,
                    sellerCity: info.city || prev.sellerCity,
                    sellerPinCode: info.pincode || prev.sellerPinCode,
                    sellerPanCardNumber: info.panNumber || prev.sellerPanCardNumber,
                    sellerStateCode: stateCode || prev.sellerStateCode,
                    sellerCountry: 'India',
                    sellerTypeId: matchedSellerTypeId || prev.sellerTypeId,
                    saleTypeId: matchedSaleTypeId || prev.saleTypeId
                }));
            } else {
                toast.error(data.message || 'GST verification failed');
                // Clear fields on failure
                setFormData(prev => ({
                    ...prev,
                    sellerTradeName: '',
                    sellerName: '',
                    sellerAddress: '',
                    sellerState: '',
                    sellerCity: '',
                    sellerPinCode: '',
                    sellerPanCardNumber: '',
                    sellerStateCode: '',
                    sellerCountry: ''
                }));
            }
        } catch (err) {
            console.error('GST verify error:', err);
            toast.error('Error verifying GST details');
            // Clear fields on error
            setFormData(prev => ({
                ...prev,
                sellerTradeName: '',
                sellerName: '',
                sellerAddress: '',
                sellerState: '',
                sellerCity: '',
                sellerPinCode: '',
                sellerPanCardNumber: '',
                sellerStateCode: '',
                sellerCountry: ''
            }));
        } finally {
            setIsVerifyingGst(false);
        }
    };

    const handleChange = (e) => {
                let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }

        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') || 
            name.toLowerCase().includes('pin_code') || 
            name.toLowerCase().includes('accountnumber') || 
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') || 
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }

        if (name === 'sellerGstNumber') {
            const upperVal = value.toUpperCase();
            setFormData(prev => ({
                ...prev,
                [name]: upperVal,
                // Clear auto-filled fields immediately on input change
                sellerTradeName: '',
                sellerName: '',
                sellerAddress: '',
                sellerState: '',
                sellerCity: '',
                sellerPinCode: '',
                sellerPanCardNumber: '',
                sellerStateCode: '',
                sellerCountry: ''
            }));
            if (upperVal.length === 15) {
                handleGSTVerify(upperVal);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };


    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();
        console.log("Submitting seller form data:", formData);

        if (isSuperAdmin && !formData.company_id) {
            toast.error("Company selection is required");
            return;
        }

        setLoading(true);
        try {
            const submissionData = { ...formData };
            if (!submissionData.sellerTypeId || submissionData.sellerTypeId === '') delete submissionData.sellerTypeId;
            if (!submissionData.saleTypeId || submissionData.saleTypeId === '') delete submissionData.saleTypeId;

            // Important: set company_id if not present for non-admins
            if (!submissionData.company_id && user?.company_id) {
                submissionData.company_id = user.company_id;
            }

            console.log("Sending Seller to API:", submissionData);
            const response = await fetchApi('/seller', {
                method: 'POST',
                body: JSON.stringify(submissionData)
            });
            console.log("Seller API Response:", response);

            if (response && (response.Status !== false)) {
                toast.success('Seller added successfully!');
                if (onSuccess) onSuccess(response.seller || response.Result || response);
                onClose();
            } else {
                toast.error(response.Error || response.message || "Failed to add seller");
            }
        } catch (error) {
            console.error("Save Error:", error);
            toast.error(error.message || "Failed to add seller");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            sellerGstNumber: '',
            sellerTradeName: '',
            sellerName: '',
            sellerPrefix: '',
            sellerEmail: '',
            sellerMobileNumber: '',
            sellerCountry: '',
            sellerState: '',
            sellerCity: '',
            sellerPinCode: '',
            sellerStateCode: '',
            sellerPanCardNumber: '',
            sellerCinNumber: '',
            sellerBankAccountName: '',
            sellerBankName: '',
            sellerAccountNo: '',
            sellerIfscCode: '',
            sellerTypeId: '',
            saleTypeId: '',
            sellerBankAddress: '',
            sellerAddress: '',
            company_id: ''
        });
    };

    if (!isOpen && !isPage) return null;

    const formContent = (
        <div className={isPage ? "page-card" : "add-seller-modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
            {!isPage && (
                <div className="add-seller-header">
                    <h2 className="add-seller-title">Add Seller</h2>
                    <button className="add-seller-close" onClick={onClose}>✕</button>
                </div>
            )}
            {isPage && <div className="page-card__title">Add Seller</div>}

            <div className={isPage ? "page-card__body" : ""}>                <form className="add-seller-form" onSubmit={handleSubmit} style={isPage ? { padding: '0' } : {}}>
                {/* Row 1: GST, Trade Name, Name, Prefix (4 fields) */}
                <div className="add-seller-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    <div className="add-seller-field-with-btn" style={{ gridColumn: 'span 2' }}>
                        <label className="add-seller-label">Seller GST</label>
                        <div className="add-seller-input-group">
                            <input
                                ref={gstRef}
                                className="add-seller-input"
                                placeholder="ENTER SELLER GST"
                                name="sellerGstNumber"
                                value={formData.sellerGstNumber}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); tradeNameRef.current?.focus(); }
                                    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); }
                                }}
                            />
                            <button
                                type="button"
                                className="add-seller-btn-verify"
                                onClick={() => handleGSTVerify(formData.sellerGstNumber)}
                                disabled={isVerifyingGst}
                            >
                                {isVerifyingGst ? 'Verifying...' : 'Verify'}
                            </button>
                        </div>
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller Trade Name</label>
                        <input
                            ref={tradeNameRef}
                            className="add-seller-input"
                            placeholder="Enter Seller Trade Name"
                            name="sellerTradeName"
                            value={formData.sellerTradeName}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); sellerNameRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); gstRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Name</label>
                        <input
                            ref={sellerNameRef}
                            className="add-seller-input"
                            placeholder="Enter Seller Name"
                            name="sellerName"
                            value={formData.sellerName}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); prefixRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); tradeNameRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Prefix</label>
                        <input
                            ref={prefixRef}
                            className="add-seller-input"
                            placeholder="Enter Seller Prefix"
                            name="sellerPrefix"
                            value={formData.sellerPrefix}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); emailRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); sellerNameRef.current?.focus(); }
                            }}
                        />
                    </div>
                </div>

                {isSuperAdmin && (
                    <div className="add-seller-row" style={{ marginTop: '-15px', marginBottom: '15px' }}>
                        <div className="add-seller-field" style={{ gridColumn: 'span 1' }}>
                            <label className="add-seller-label">Select Company</label>
                            <select
                                ref={companyRef}
                                className="add-seller-select"
                                name="company_id"
                                value={formData.company_id}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); emailRef.current?.focus(); }
                                    if (e.key === 'ArrowLeft') { e.preventDefault(); prefixRef.current?.focus(); }
                                }}
                            >
                                <option value="">--Select Company--</option>
                                {Array.isArray(companies) && companies.map(c => (
                                    <option key={c._id || c.id} value={c._id || c.id}>{c.companyName}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Row 2: Email, Mobile, Country, State, City (5 fields) */}
                <div className="add-seller-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    <div className="add-seller-field">
                        <label className="add-seller-label">Email</label>
                        <input
                            ref={emailRef}
                            className="add-seller-input"
                            type="email"
                            placeholder="Enter Seller Email"
                            name="sellerEmail"
                            value={formData.sellerEmail}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); mobileRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
                                    e.preventDefault();
                                    if (isSuperAdmin) companyRef.current?.focus();
                                    else prefixRef.current?.focus();
                                }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Mobile Number</label>
                        <input
                            ref={mobileRef}
                            className="add-seller-input"
                            placeholder="Enter Seller Mobile Num"
                            name="sellerMobileNumber"
                            value={formData.sellerMobileNumber}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); countryRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); emailRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller Country</label>
                        <CreatableSelect
                            ref={countryRef}
                            value={formData.sellerCountry}
                            options={countryList}
                            onChange={(val) => setFormData(prev => ({ ...prev, sellerCountry: val }))}
                            onAddOption={(newVal) => {
                                handleAddCountry(newVal);
                                setFormData(prev => ({ ...prev, sellerCountry: newVal }));
                            }}
                            placeholder="Select or Type Country"
                            onSelect={() => stateRef.current?.focus()}
                            onPrevField={() => mobileRef.current?.focus()}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller State</label>
                        <CreatableSelect
                            ref={stateRef}
                            value={formData.sellerState}
                            options={stateList}
                            onChange={(val) => setFormData(prev => ({ ...prev, sellerState: val }))}
                            onAddOption={(newVal) => {
                                handleAddState(newVal);
                                setFormData(prev => ({ ...prev, sellerState: newVal }));
                            }}
                            placeholder="Select or Type State"
                            onSelect={() => cityRef.current?.focus()}
                            onPrevField={() => countryRef.current?.focus()}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller City</label>
                        <CreatableSelect
                            ref={cityRef}
                            value={formData.sellerCity}
                            options={cityList}
                            onChange={(val) => setFormData(prev => ({ ...prev, sellerCity: val }))}
                            onAddOption={(newVal) => {
                                handleAddCity(newVal);
                                setFormData(prev => ({ ...prev, sellerCity: newVal }));
                            }}
                            placeholder="Select or Type City"
                            onSelect={() => pinRef.current?.focus()}
                            onPrevField={() => stateRef.current?.focus()}
                        />
                    </div>
                </div>

                {/* Row 3: Pin, State Code, PAN, CIN, Bank Account Name (5 fields) */}
                <div className="add-seller-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller Pin Code</label>
                        <input
                            ref={pinRef}
                            className="add-seller-input"
                            placeholder="Enter Seller Pin Code"
                            name="sellerPinCode"
                            value={formData.sellerPinCode}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); stateCodeRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); cityRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller State Code</label>
                        <input
                            ref={stateCodeRef}
                            className="add-seller-input"
                            placeholder="Enter Seller State Code"
                            name="sellerStateCode"
                            value={formData.sellerStateCode}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); panRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); pinRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller PAN No</label>
                        <input
                            ref={panRef}
                            className="add-seller-input"
                            placeholder="Enter Seller PAN No"
                            name="sellerPanCardNumber"
                            value={formData.sellerPanCardNumber}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); cinRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); stateCodeRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller CIN Number</label>
                        <input
                            ref={cinRef}
                            className="add-seller-input"
                            placeholder="Enter Seller CIN Number"
                            name="sellerCinNumber"
                            value={formData.sellerCinNumber}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); bankAccountNameRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); panRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Bank Account Name</label>
                        <input
                            ref={bankAccountNameRef}
                            className="add-seller-input"
                            placeholder="Enter Bank Account Name"
                            name="sellerBankAccountName"
                            value={formData.sellerBankAccountName}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); bankNameRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); cinRef.current?.focus(); }
                            }}
                        />
                    </div>
                </div>

                {/* Row 4: Bank Name, Bank A/C No, IFSC, Seller Type (4 fields) */}
                <div className="add-seller-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className="add-seller-field">
                        <label className="add-seller-label">Bank Name</label>
                        <input
                            ref={bankNameRef}
                            className="add-seller-input"
                            placeholder="Enter Bank Name"
                            name="sellerBankName"
                            value={formData.sellerBankName}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); accountNoRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); bankAccountNameRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Bank A/C No.</label>
                        <input
                            ref={accountNoRef}
                            className="add-seller-input"
                            placeholder="Enter Bank A/C No."
                            name="sellerAccountNo"
                            value={formData.sellerAccountNo}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); ifscRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); bankNameRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">IFSC Code</label>
                        <input
                            ref={ifscRef}
                            className="add-seller-input"
                            placeholder="Enter IFSC Code"
                            name="sellerIfscCode"
                            value={formData.sellerIfscCode}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); sellerTypeRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); accountNoRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller Type</label>
                        <CreatableSelect
                            ref={sellerTypeRef}
                            value={sellerTypes.find(st => (st.id || st._id || st.customerTypeId) === formData.sellerTypeId)?.customer_type_name || ''}
                            options={sellerTypes.map(st => st.customer_type_name || st.customerTypeName || st.name)}
                            onChange={(val) => {
                                const found = sellerTypes.find(st => (st.customer_type_name || st.customerTypeName || st.name) === val);
                                if (found) setFormData(prev => ({ ...prev, sellerTypeId: found.id || found._id || found.customerTypeId }));
                            }}
                            onAddOption={(newVal) => handleAddSellerType(newVal)}
                            placeholder="Select Seller Type"
                            onSelect={() => bankAddrRef.current?.focus()}
                            onPrevField={() => ifscRef.current?.focus()}
                        />
                    </div>
                </div>

                {/* Row 5: Bank Address, Seller Address (Textareas) */}
                <div className="add-seller-row-2">
                    <div className="add-seller-field">
                        <label className="add-seller-label">Bank Address</label>
                        <textarea
                            ref={bankAddrRef}
                            className="add-seller-textarea"
                            placeholder="Bank Address"
                            name="sellerBankAddress"
                            value={formData.sellerBankAddress}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addrRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); sellerTypeRef.current?.focus(); }
                            }}
                        />
                    </div>

                    <div className="add-seller-field">
                        <label className="add-seller-label">Seller Address</label>
                        <textarea
                            ref={addrRef}
                            className="add-seller-textarea"
                            placeholder="Seller Address"
                            name="sellerAddress"
                            value={formData.sellerAddress}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitRef.current?.focus(); }
                                if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); bankAddrRef.current?.focus(); }
                            }}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="add-seller-actions">
                    {!isPage && <button type="button" className="add-seller-btn-reset" onClick={handleReset}>Reset</button>}
                    {isPage && <button type="button" className="add-seller-btn-reset" onClick={onClose} style={{ background: '#fff', border: '1px solid #ddd', color: '#333' }}>Cancel</button>}
                    <button
                        type="submit"
                        ref={submitRef}
                        className="add-seller-btn-submit"
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowLeft') { e.preventDefault(); addrRef.current?.focus(); }
                        }}
                    >Submit</button>
                </div>
            </form>
            </div>
        </div>
    );

    if (isPage) return formContent;

    return (
        <div className="add-seller-modal-overlay" onClick={onClose}>
            {formContent}
        </div>
    );
}

export default AddSellerModal;
